import { useEffect } from "react";
import { useForm } from "react-hook-form";
import styles from "./profileform.module.css";

import {
  Input,
  Button,
  SubHeadingTitle,
} from "../../../../../components/shared";

import { Link } from "react-router-dom";
import Swal from "sweetalert2";

import {
  useUserProfile,
  useAddUserProfile,
  useUpdateUserProfile,
} from "../../../../../queries/useUserProfile";

const ProfileForm = () => {
  const { data: profileRes, isLoading } = useUserProfile();
const today = new Date().toISOString().split("T")[0];
  const addMutation = useAddUserProfile();
  const updateMutation = useUpdateUserProfile();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      email: "",
      mobile: "",
      gender: "",
      dob: "",
      age: "",
      subscribe: true,
    },
  });

  /*
   * IMPORTANT:
   * API response:
   *
   * {
   *   status: true,
   *   user: {
   *      id: 7,
   *      username: "Customer",
   *      ...
   *   }
   * }
   *
   * So use profileRes.user, NOT profileRes.data
   */
  const profileData = profileRes?.user;

  const isUpdateMode = !!profileData?.id;

  const dob = watch("dob");
  const selectedGender = watch("gender");

  /* =========================
     Calculate Age
  ========================= */
  useEffect(() => {
    if (!dob) {
      setValue("age", "");
      return;
    }

    const birthDate = new Date(dob);
    const today = new Date();

    let age =
      today.getFullYear() - birthDate.getFullYear();

    const monthDiff =
      today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 &&
        today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    setValue("age", age);
  }, [dob, setValue]);

  /* =========================
     Set Existing Profile Data
  ========================= */
  useEffect(() => {
    if (!profileData) return;

    console.log("Profile API data:", profileData);

    reset({
      name: profileData.username || "",
      email: profileData.email || "",
      mobile: profileData.phone_number || "",
      gender: profileData.gender || "",
      dob: profileData.dob
        ? profileData.dob.split("T")[0]
        : "",
      age: "",
      subscribe: profileData.subscribe ?? true,
    });
  }, [profileData, reset]);

  /* =========================
     Submit
  ========================= */
  const onSubmit = async (formData) => {
    try {
      const payload = new FormData();

      payload.append("username", formData.name);
      payload.append("email", formData.email);
      payload.append("phone_number", formData.mobile);
      payload.append("gender", formData.gender);
      payload.append("dob", formData.dob);
      payload.append(
        "subscribe",
        formData.subscribe ? "1" : "0"
      );

      if (!isUpdateMode) {
        await addMutation.mutateAsync(payload);

        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Profile added successfully",
          confirmButtonColor: "#FB6C22",
        });
      } else {
        await updateMutation.mutateAsync(payload);

        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Profile updated successfully",
          confirmButtonColor: "#1da7a3",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error?.message ||
          "Profile save failed",
        confirmButtonColor: "#d33",
      });
    }
  };

  /* =========================
     Delete Account
  ========================= */
  const handleDeleteAccount = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you really want to delete your account? This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          icon: "success",
          title: "Request Received",
          text: "Your request has been received. Our technical team will review and process your account deletion shortly.",
          confirmButtonColor: "#127d17",
        });
      }
    });
  };

  if (isLoading) {
    return (
      <div className={styles.loading}>
        Loading profile...
      </div>
    );
  }

  return (
    <form
      className={styles.container}
      onSubmit={handleSubmit(onSubmit)}
    >
      {/* =========================
          Row 1
      ========================= */}
      <div className={styles.formRow}>
        <div className={styles.field}>
          <Input
            label="Name"
            name="name"
            placeholder="Name"
            register={register}
            validation={{
              required: "Name is required",
            }}
            error={errors?.name?.message}
          />
        </div>

        <div className={styles.field}>
         <Input
  name="mobile"
  label="Mobile Number"
  placeholder="xxxxxxxxxx"
  register={register}
  validation={{
    required: "Mobile number is required",
    pattern: {
      value: /^[6-9]\d{9}$/,
      message: "Enter a valid 10-digit mobile number",
    },
  }}
  error={errors?.mobile?.message}
/>
        </div>
      </div>

      {/* =========================
          Row 2
      ========================= */}
      <div className={styles.formRow}>
        <div className={styles.field}>
          <Input
  label="Email"
  name="email"
  placeholder="Enter email"
  register={register}
  validation={{
    required: "Email is required",
    pattern: {
      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: "Enter a valid email",
    },
  }}
  error={errors?.email?.message}
/>
        </div>

        <div className={styles.field}>
         <Input
  label="Date of Birth"
  name="dob"
  type="date"
  register={register}
  validation={{
    required: "Date of Birth is required",
    validate: (value) => {
      if (value > today) {
        return "Date of Birth cannot be in the future";
      }
      return true;
    },
  }}
  error={errors?.dob?.message}
  max={today}
/>
        </div>
      </div>

      {/* =========================
          Row 3
      ========================= */}
      <div className={styles.formRow}>
        <div className={styles.field}>
          <Input
            label="Age"
            name="age"
            register={register}
            readOnly
          />
        </div>

        <div className={styles.genderWrapper}>
          <GenderSelector
            selectedGender={selectedGender}
            onSelect={(gender) =>
              setValue("gender", gender)
            }
          />
        </div>
      </div>

      {/* =========================
          Newsletter
      ========================= */}
      <div className={styles.checkboxContainer}>
        <input
          type="checkbox"
          className={styles.subscribe}
          {...register("subscribe")}
        />

        <label>
          Keep up with our latest news and events.
          Subscribe to our newsletter.
        </label>
      </div>

      {/* =========================
          Submit Button
      ========================= */}
      <div className={styles.buttonContainer}>
        <Button
          title={
            isUpdateMode
              ? "Update Profile"
              : "Add Profile"
          }
          type="submit"
          style={styles.updateBtn}
          disabled={
            addMutation.isPending ||
            updateMutation.isPending
          }
        />
      </div>

      {/* =========================
          Delete Account
      ========================= */}
      <p className={styles.deleteText}>
        Do you want to delete your account permanently?{" "}
        <Link
          className={styles.link}
          to="#"
          onClick={(e) => {
            e.preventDefault();
            handleDeleteAccount();
          }}
        >
          Click here
        </Link>
      </p>
    </form>
  );
};

/* =========================
   Gender Selector
========================= */

const GenderSelector = ({
  selectedGender,
  onSelect,
}) => {
  const genders = [
    "male",
    "female",
    "others",
  ];

  return (
    <div className={styles.genderContainer}>
      <SubHeadingTitle
        style={styles.label}
        subTitle="Gender (optional)"
      />

      <div className={styles.buttonGroup}>
        {genders.map((gender) => (
          <Button
            key={gender}
            title={
              gender.charAt(0).toUpperCase() +
              gender.slice(1)
            }
            type="button"
            onHandler={() => onSelect(gender)}
            style={`${styles.genderBtn} ${
              selectedGender === gender
                ? styles.active
                : ""
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default ProfileForm;